/**
 * VisibilityControlsPanel
 *
 * Allows Super Admins to control template visibility:
 * - All Tenants: Available to everyone
 * - Specific Plans: Only tenants on selected plans
 * - Specific Tenants: Only selected tenant accounts
 * - Hidden: Not visible to any tenants
 */
import { useState, useEffect } from 'react';
import { cn, Label, Button } from '@respectly/ui';
import { Info, Check } from 'lucide-react';
import { getApiClient } from '@respectly/api-client';

// ============================================
// Types
// ============================================

type Visibility = 'all' | 'plans' | 'tenants' | 'none';

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number;
  isActive: boolean;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  planId: string;
}

export interface VisibilityData {
  visibility: Visibility;
  visibleToPlans: string[];
  visibleToTenants: string[];
}

export interface VisibilityControlsPanelProps {
  visibility: Visibility;
  visibleToPlans: string[];
  visibleToTenants: string[];
  onChange: (data: VisibilityData) => void;
  className?: string;
}

// ============================================
// Component
// ============================================

export function VisibilityControlsPanel({
  visibility,
  visibleToPlans,
  visibleToTenants,
  onChange,
  className,
}: VisibilityControlsPanelProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);

  // Load plans when visibility is 'plans'
  useEffect(() => {
    if (visibility === 'plans' && plans.length === 0) {
      loadPlans();
    }
  }, [visibility]);

  // Load tenants when visibility is 'tenants'
  useEffect(() => {
    if (visibility === 'tenants' && tenants.length === 0) {
      loadTenants();
    }
  }, [visibility]);

  const loadPlans = async () => {
    setIsLoadingPlans(true);
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: Plan[];
      }>('/api/plans?limit=100');
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const loadTenants = async () => {
    setIsLoadingTenants(true);
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: { tenants: Tenant[] };
      }>('/api/accounts?limit=100');
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const handleVisibilityChange = (newVisibility: Visibility) => {
    onChange({
      visibility: newVisibility,
      visibleToPlans: newVisibility === 'plans' ? visibleToPlans : [],
      visibleToTenants: newVisibility === 'tenants' ? visibleToTenants : [],
    });
  };

  const handlePlanToggle = (planId: string) => {
    const newPlans = visibleToPlans.includes(planId)
      ? visibleToPlans.filter((id) => id !== planId)
      : [...visibleToPlans, planId];

    onChange({
      visibility,
      visibleToPlans: newPlans,
      visibleToTenants,
    });
  };

  const handleTenantToggle = (tenantId: string) => {
    const newTenants = visibleToTenants.includes(tenantId)
      ? visibleToTenants.filter((id) => id !== tenantId)
      : [...visibleToTenants, tenantId];

    onChange({
      visibility,
      visibleToPlans,
      visibleToTenants: newTenants,
    });
  };

  const visibilityOptions = [
    {
      value: 'all' as const,
      label: 'All Tenants',
      description: 'Available to all tenant accounts. Template will appear in all template galleries.',
      icon: '🌐',
    },
    {
      value: 'plans' as const,
      label: 'Specific Plans',
      description: 'Only visible to tenants on selected subscription plans. Useful for premium features.',
      icon: '💎',
    },
    {
      value: 'tenants' as const,
      label: 'Specific Tenants',
      description: 'Only visible to selected tenant accounts. Useful for custom or client-specific templates.',
      icon: '🎯',
    },
    {
      value: 'none' as const,
      label: 'Hidden',
      description: 'Not visible to any tenants. Use for drafts or deprecated templates.',
      icon: '🔒',
    },
  ];

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-start gap-2">
        <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <Label className="text-base font-semibold text-slate-900 dark:text-white">
            Template Visibility
          </Label>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Control which tenants can see and use this template in their galleries.
          </p>
        </div>
      </div>

      {/* Visibility Options */}
      <div className="space-y-3">
        {visibilityOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleVisibilityChange(option.value)}
            className={cn(
              'w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left',
              'hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/20',
              visibility === option.value
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
            )}
          >
            {/* Icon */}
            <span className="text-2xl flex-shrink-0 mt-0.5">{option.icon}</span>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900 dark:text-white">
                  {option.label}
                </span>
                {visibility === option.value && (
                  <Check className="h-4 w-4 text-blue-500 flex-shrink-0" />
                )}
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {option.description}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Plan Selection */}
      {visibility === 'plans' && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-slate-900 dark:text-white">
            Select Plans
          </Label>
          {isLoadingPlans ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>No plans available</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadPlans}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => handlePlanToggle(plan.id)}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border transition-all text-left',
                    'hover:border-blue-300',
                    visibleToPlans.includes(plan.id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                  )}
                >
                  <div
                    className={cn(
                      'h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                      visibleToPlans.includes(plan.id)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-slate-300 dark:border-slate-600'
                    )}
                  >
                    {visibleToPlans.includes(plan.id) && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-white">
                      {plan.name}
                    </div>
                    <div className="text-sm text-slate-500">
                      ${plan.monthlyPrice}/month
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tenant Selection */}
      {visibility === 'tenants' && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-slate-900 dark:text-white">
            Select Tenants
          </Label>
          {isLoadingTenants ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>No tenants available</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadTenants}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  type="button"
                  onClick={() => handleTenantToggle(tenant.id)}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border transition-all text-left',
                    'hover:border-blue-300',
                    visibleToTenants.includes(tenant.id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                  )}
                >
                  <div
                    className={cn(
                      'h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                      visibleToTenants.includes(tenant.id)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-slate-300 dark:border-slate-600'
                    )}
                  >
                    {visibleToTenants.includes(tenant.id) && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-white">
                      {tenant.name}
                    </div>
                    <div className="text-sm text-slate-500">
                      @{tenant.slug} · {tenant.status}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {(visibility === 'plans' && visibleToPlans.length > 0) ||
      (visibility === 'tenants' && visibleToTenants.length > 0) ? (
        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {visibility === 'plans' ? (
              <>
                Template will be visible to <strong>{visibleToPlans.length}</strong>{' '}
                selected plan{visibleToPlans.length !== 1 ? 's' : ''}.
              </>
            ) : (
              <>
                Template will be visible to <strong>{visibleToTenants.length}</strong>{' '}
                selected tenant{visibleToTenants.length !== 1 ? 's' : ''}.
              </>
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
